import { faLessThanEqual } from "@fortawesome/free-solid-svg-icons";
import { Context } from "./context.js";
import { createSlice } from "@reduxjs/toolkit";
import React, { useState, useEffect, useRef } from "react";
import { useCallback } from "react";
import * as RB from "react-bootstrap";
import { useSelector, useDispatch, useStore } from "react-redux";
import { textSpanIntersectsWithTextSpan } from "typescript";
import { MAX_CANVAS_HEIGHT,MAX_CANVAS_WIDTH,MIN_CANVAS_HEIGHT,MIN_CANVAS_WIDTH } from "../../pages/params.js";
//https://developer.mozilla.org/en-US/docs/Web/HTML/Element/canvas

const isValid = (w, h) => {
    return (w > MIN_CANVAS_WIDTH && w < MAX_CANVAS_WIDTH
            && h > MIN_CANVAS_HEIGHT && h < MAX_CANVAS_HEIGHT);
};
export const scaleToolSlice = createSlice({
    name: "scaleTool",
    initialState: {
        lock: true,
        newW: "",
        newH: "",
        trigger: true,
    },
    reducers: {
        initState: (state, action) => {
            let w = action.payload.width,
                h = action.payload.height;
            let lock = action.payload.lock;
            state.newW = w;
            state.newH = h;
            state.lock = lock;
        },
        updateWidth: (state, action) => {
            let w = action.payload.value;
            let k = state.newW / state.newH;
            let h = w / k;
            if (state.lock) {
                if (isValid(w, h)) {
                    state.newW = w;
                    state.newH = h;
                    return;
                }
            }
            if (isValid(w, state.newH)) {
                state.newW = w;
            }
        },
        updateHeight: (state, action) => {
            let h = action.payload.value;
            let k = state.newW / state.newH;
            let w = h * k;
            if (state.lock) {
                if (isValid(w, h)) {
                    state.newW = w;
                    state.newH = h;
                    // return;
                }
            }
            if (isValid(state.newW, h)) {
                state.newH = h;
            }
        },
        updateWH: (state, action) => {
            let w = action.payload.width;
            let h = action.payload.height;
            if (state.lock) {
                let k = state.newW / state.newH;
                if (w > h) {
                    h = Math.round(w / k);
                } else {
                    w = Math.round(k * h);
                }
            }
            if (isValid(w, h)) {
                state.newH = h;
                state.newW = w;
            }
        },
        updateLock: (state, action) => {
            state.lock = action.payload.lock;
        },
        triggerUpdate: (state, action) => {
            state.trigger = !state.trigger;
        },
    },
});
const {
    initState,
    updateWidth,
    updateHeight,
    updateLock,
    updateWH,
    triggerUpdate,
} = scaleToolSlice.actions;
export class ScaleCnvControls {
    /**@type {Context} */
    context;
    /**@type {import("redux").Store} */
    store;
    #dragInd = -1;
    rad = 6;
    listeners = {};
    #sX = 0.2;
    #sY = 0.2;
    static scaleCtrl = null;
    timerId = null;
    #stPoint = null;
    #curSize = null;
    #curViewSize = null;
    #keepScale = true;
    static getInstance(context, store) {
        if (this.scaleCtrl === null) {
            this.scaleCtrl = new ScaleCnvControls(context, store);
        }
        return this.scaleCtrl;
    }
    constructor(context, store) {
        this.context = context;
        this.store = store;
        this.listeners = {
            mousedown: this.mDown,
            mousemove: this.mMove,
            mouseup: this.mUp,
            mouseleave: this.mLeave,
        };
    }
    getNewSize = () => {
        let state = this.store.getState();
        let newW = state.scaleTool.newW;
        let newH = state.scaleTool.newH;
        return [newW, newH];
    };
    calcPts = (size) => {
        let w = size[0],
            h = size[1];
        let sx = Math.round(this.#sX * w);
        let sy = Math.round(this.#sY * h);
        let lt = [sx, sy],
            rb = [w - sx, h - sy];
        let hlt = [Math.round((rb[0] + lt[0]) / 2), lt[1]];
        let hrb = [rb[0], Math.round((rb[1] + lt[1]) / 2)];
        let pts = [
            lt,
            hlt,
            [rb[0], lt[1]],
            hrb,
            rb,
            [hlt[0], rb[1]],
            [lt[0], rb[1]],
            [lt[0], hrb[1]],
        ];
        return pts;
    };
    initState = () => {
        this.store.dispatch(
            initState({
                width: this.context.offScrCnv.width,
                height: this.context.offScrCnv.height,
                lock: false,
            })
        );
    };
    updateImage = (width = false, height = false) => {
        let [newW, newH] = this.getNewSize();
        let oldW = this.context.offScrCnv.width;
        let oldH = this.context.offScrCnv.height;
        this.context.tmpCnv.width = newW;
        this.context.tmpCnv.height = newH;
        this.context.tmpCnvCtx.scale(newW / oldW, newH / oldH);
        this.context.tmpCnvCtx.drawImage(this.context.offScrCnv, 0, 0);

        // let imgData = this.context.tmpCnvCtx.getImageData(0, 0, newW, newH);
        this.context.curCnv.width = newW;
        this.context.curCnv.height = newH;
        this.context.curCnvCtx.drawImage(this.context.tmpCnv, 0, 0);

        this.context.tmpCnvCtx.clearRect(0, 0, newW, newH);
        this.context.tmpCnvCtx.setTransform(1, 0, 0, 1, 0, 0);
        this.context.tmpCnvCtx.drawImage(this.context.curCnv, 0, 0);
        this.context.updateImage(this.context.tmpCnv, this.#keepScale);

        this.drawConrols();
    };
    delayedUpdateImage = () => {
        if (this.timerId !== null) {
            clearTimeout(this.timerId);
        }
        this.timerId = setTimeout(() => {
            this.updateImage();
        }, 10);
    };
    drawConrols = () => {
        let w = this.context.tmpCnv.width;
        let h = this.context.tmpCnv.height;
        let sx = Math.round(this.#sX * w);
        let sy = Math.round(this.#sY * h);
        let lt = [sx, sy],
            rb = [w - sx, h - sy];
        let hlt = [Math.round((rb[0] + lt[0]) / 2), lt[1]];
        let hrb = [rb[0], Math.round((rb[1] + lt[1]) / 2)];
        let pts = [
            lt,
            hlt,
            [rb[0], lt[1]],
            hrb,
            rb,
            [hlt[0], rb[1]],
            [lt[0], rb[1]],
            [lt[0], hrb[1]],
        ];

        let tr = this.context.cnvCtx.getTransform();
        this.context.cnvCtx.strokeStyle = "purple";
        pts = pts.map(
            (pt) => this.context.canvasToView(pt, this.context.tmpCnv),
            this.context
        );
        // pts = pts.map((pt) => this.context.canvasToView(pt, this.context.cnv), this.context);

        this.context.cnvCtx.lineWidth = 3;
        this.context.cnvCtx.setTransform(1, 0, 0, 1, 0, 0);
        for (let i = 0; i < pts.length; ++i) {
            let next = (i + 1) % pts.length;
            this.context.cnvCtx.beginPath();
            this.context.cnvCtx.moveTo(pts[i][0], pts[i][1]);
            this.context.cnvCtx.lineTo(pts[next][0], pts[next][1]);
            this.context.cnvCtx.stroke();
        }
        for (let i = 0; i < pts.length; ++i) {
            this.context.cnvCtx.beginPath();
            this.context.cnvCtx.arc(
                pts[i][0],
                pts[i][1],
                this.rad,
                2 * Math.PI,
                false
            );
            this.context.cnvCtx.fillStyle = "yellow";
            this.context.cnvCtx.strokeStyle = "#000033";
            this.context.cnvCtx.fill();
            this.context.cnvCtx.lineWidth = 1;
            this.context.cnvCtx.stroke();
        }
        this.context.cnvCtx.setTransform(tr);
    };
    #checkClick = (point) => {
        let size = this.getNewSize();
        let size2 = [
            this.context.offScrCnv.width,
            this.context.offScrCnv.height,
        ];
        let pts = this.calcPts(size2);
        pts = pts.map(this.context.orToView.bind(this.context));
        for (let i = 0; i < pts.length; ++i) {
            let lt = [pts[i][0] - this.rad, pts[i][1] - this.rad];
            let rb = [pts[i][0] + this.rad, pts[i][1] + this.rad];
            if (
                lt[0] < point[0] &&
                lt[1] < point[1] &&
                rb[0] > point[0] &&
                rb[1] > point[1]
            ) {
                return [i, pts[i]];
            }
        }
        return [-1, null];
    };
    mDown = (event) => {
        this.drawConrols();
        const res = this.#checkClick([event.offsetX, event.offsetY]);
        this.#dragInd = res[0];
        this.#keepScale = true;
        if (res[1] !== null) {
            const pt = res[1];
            // this.stPoint = this.context.canvasToView(pt, this.context.tmpCnv);
            this.#stPoint = pt;
            this.#curSize = this.getNewSize();
            this.#curViewSize = [this.context.imgW, this.context.imgH];
        }
    };
    mMove = (event) => {
        if (this.#dragInd > -1) {
            let vW = this.context.cnv.width,
                vH = this.context.cnv.height;
            let orW = this.context.offScrCnv.width,
                orH = this.context.offScrCnv.height;

            let newSize = this.getNewSize();

            let size = this.#curSize;

            let pts = this.calcPts(newSize);
            pts = pts.map(
                (pt) => this.context.canvasToView(pt, this.context.tmpCnv),
                this.context
            );

            let strPt = this.#stPoint;
            let finPt = [event.offsetX, event.offsetY];
            let kX =
                ((finPt[0] - strPt[0]) * 2) /
                (this.#curViewSize[0] * (1 - this.#sX * 2));
            let kY =
                ((finPt[1] - strPt[1]) * 2) /
                (this.#curViewSize[1] * (1 - this.#sY * 2));
            //Points scheme:
            // 0**1**2
            // *     *
            // 7     3
            // *     *
            // 6**5**4
            switch (this.#dragInd) {
                case 3:
                    this.store.dispatch(
                        updateWidth({ value: size[0] * (1 + kX) })
                    );
                    break;
                case 7:
                    this.store.dispatch(
                        updateWidth({ value: size[0] * (1 - kX) })
                    );
                    break;
                case 1:
                    this.store.dispatch(
                        updateHeight({ value: size[1] * (1 - kY) })
                    );
                    break;
                case 5:
                    this.store.dispatch(
                        updateHeight({ value: size[1] * (1 + kY) })
                    );
                    break;
                case 6:
                    this.store.dispatch(
                        updateWH({
                            width: size[0] * (1 - kX),
                            height: size[1] * (1 + kY),
                        })
                    );
                    break;
                case 4:
                    this.store.dispatch(
                        updateWH({
                            width: size[0] * (1 + kX),
                            height: size[1] * (1 + kY),
                        })
                    );
                    break;
                case 2:
                    this.store.dispatch(
                        updateWH({
                            width: size[0] * (1 + kX),
                            height: size[1] * (1 - kY),
                        })
                    );
                    break;
                case 0:
                    this.store.dispatch(
                        updateWH({
                            width: size[0] * (1 - kX),
                            height: size[1] * (1 - kY),
                        })
                    );
                    break;
            }
        }
    };
    mUp = (event) => {
        this.#dragInd = -1;
        this.#keepScale = false;
        this.store.dispatch(triggerUpdate({}));
    };
    mLeave = (event) => {
        if (this.#dragInd != -1) {
            this.#dragInd = -1;
            this.#keepScale = false;
            this.store.dispatch(triggerUpdate({}));
        }
    };
}
export const ScaleProps = (props) => {
    /**@type {Context} */
    const context = props.context;
    const newW = useSelector((state) => state.scaleTool.newW);
    const newH = useSelector((state) => state.scaleTool.newH);
    const lock = useSelector((state) => state.scaleTool.lock);
    const store = useStore();
    const wRef = useRef(null);
    const hRef = useRef(null);
    // const [whstr, setWhstr] = useState('');
    const [dummy, setDummy] = useState(0);
    let whstr;
    const dispatch = useDispatch();
    // useEffect(() => {
    //     if (props.context.offScrCnv) {
    //         curW = props.context.offScrCnv.width;
    //         curH = props.context.offScrCnv.height;
    //     }
    // }, [props.context.offScrCnv.width, props.context.offScrCnv.height])
    const widthChange = (event) => {
        let newVal = Math.round(Number(event.target.value));
        dispatch(updateWidth({ width: newVal }));
    };
    const heightChange = (event) => {
        let newVal = Math.round(Number(event.target.value));
        dispatch(updateHeight({ height: newVal }));
    };
    const handleLockChange = (event) => {
        let newVal = event.target.checked;
        dispatch(updateLock({ lock: newVal }));
    };
    const applyHandle = (event) => {
        let oldW = context.offScrCnv.width,
            oldH = context.offScrCnv.height;
        let scaleX = newW / oldW,
            scaleY = newH / oldH;

        context.curCnv.width = newW;
        context.curCnv.height = newH;
        context.curCnvCtx.scale(scaleX, scaleY);
        context.curCnvCtx.drawImage(context.offScrCnv, 0, 0);
        context.offScrCnv.width = newW;
        context.offScrCnv.height = newH;
        context.offScrCnvCtx.clearRect(0, 0, newW, newH);
        context.offScrCnvCtx.drawImage(context.curCnv, 0, 0);
        context.updateImage();
        context.putToHistory();

        setDummy(dummy + 1); //Fore component to re-render to update `whstr`
    };
    const resetHandle = (event) => {
        dispatch(
            initState({
                width: context.offScrCnv.width,
                height: context.offScrCnv.height,
                lock: lock,
            })
        );
    };
    if (props.context.offScrCnv) {
        let w = context.offScrCnv.width,
            h = context.offScrCnv.height;
        // setWhstr(`${w}x${h}`);]
        whstr = `${w}x${h}`;
    }

    useEffect(() => {
        wRef.current.addEventListener("change", (event) => {
            let newVal = Math.round(Number(wRef.current.value));
            dispatch(updateWidth({ value: newVal }));
            //We use store.getState() beacause newW will be updated only on the
            //next render
            wRef.current.value = store.getState().scaleTool.newW;
        });
        hRef.current.addEventListener("change", (event) => {
            let newVal = Math.round(Number(hRef.current.value));
            dispatch(updateHeight({ value: newVal }));
            //We use store.getState() beacause newH will be updated only on the
            //next render
            wRef.current.value = store.getState().scaleTool.newW;
        });
    }, []); // Warning is ok.
    useEffect(() => {
        wRef.current.value = Math.round(newW);
        hRef.current.value = Math.round(newH);
    }, [newW, newH]);
    const form = (
        <RB.Form>
            <RB.FormGroup className="scaleprop">
                <RB.Form.Row>
                    <RB.Form.Label size="sm">
                        Текущий размер (ШхВ): {whstr}
                    </RB.Form.Label>
                </RB.Form.Row>
                <RB.Form.Row>
                    <RB.Col>
                        <RB.Form.Label size="sm">Ширина</RB.Form.Label>
                    </RB.Col>
                    <RB.Col>
                        <RB.Form.Control
                            ref={wRef}
                            size="sm"
                            type="number"
                        ></RB.Form.Control>
                    </RB.Col>
                </RB.Form.Row>
                <RB.Form.Row>
                    <RB.Col>
                        <RB.Form.Label size="sm">Высота</RB.Form.Label>
                    </RB.Col>
                    <RB.Col>
                        <RB.Form.Control
                            ref={hRef}
                            size="sm"
                            type="number"
                        ></RB.Form.Control>
                    </RB.Col>
                </RB.Form.Row>
                <RB.Form.Row>
                    <RB.Form.Check
                        type="checkbox"
                        checked={lock}
                        label="Сохранить соотношение сторон"
                        onChange={handleLockChange}
                    ></RB.Form.Check>
                </RB.Form.Row>
                <RB.Form.Row className="button-row">
                    <RB.Button onClick={applyHandle} size="sm">
                        Применить
                    </RB.Button>
                    <RB.Button onClick={resetHandle} size="sm">
                        Сброс
                    </RB.Button>
                </RB.Form.Row>
            </RB.FormGroup>
        </RB.Form>
    );
    return form;
};
